// @ts-check
import { createLog } from '../utils/logger'

const log = createLog()

class TimeoutError extends Error {
  constructor (ms) {
    super(`Exceeded ${ms}`)
  }
}

const timeout = ms =>
  new Promise(resolve => setTimeout(resolve, ms))

const errorAfter = async (ms, error = new TimeoutError(ms)) => {
  await timeout(ms)
  throw error
}

const raceWithin = (ms, promise, error) =>
  Promise.race([promise, errorAfter(ms, error)])

const repeatUntil = (predicate, interval = 10, timeout = Number.MAX_SAFE_INTEGER) => new Promise((resolve, reject) => {
  const start = Date.now()
  const check = async () => {
    const result = await predicate()
    if (!result) {
      if (Date.now() - start < timeout) {
        setTimeout(check, interval)
      } else {
        reject(new Error(`Timeout: exceeded ${timeout}ms`))
      }
    } else {
      resolve(result)
    }
  }
  check()
})

const request = (opts) => {
  let method = 'GET'
  let url
  let timeout = 3000
  let headers = {}
  let data
  if (typeof opts === 'string') {
    url = opts
  } else {
    ({ method = 'GET', url, timeout = 3000, headers = {}, data } = opts)
  }
  const xhttp = new XMLHttpRequest()

  const promise = new Promise((resolve, reject) => {
    xhttp.onreadystatechange = function () {
      if (this.readyState === 4) {
        if (this.status > 199 && this.status < 300) {
          resolve(this.responseText)
        } else {
          reject(new Error(`Error status ${this.status}: ${this.statusText}`))
        }
      }
    }
    xhttp.open(method, url, true)
    Object.entries(headers).forEach(([k, v]) => xhttp.setRequestHeader(k, v))
    if (data) {
      xhttp.send(data)
    } else {
      xhttp.send()
    }
  })

  return timeout ? raceWithin(timeout, promise) : promise
}

export default class Persist {
  /**
   * @type {string|undefined}
   */
  #backendUrl

  /**
   * @type {string|undefined}
   */
  #channel

  /**
   * @type {string|undefined}
   */
  #platform

  /**
   * @type {string}
   */
  #domain

  /**
   * @type {'tcfv2'|'lgpd'}
   */
  #cmpModel

  /**
   * @param {PersistOpts} opts
   */
  constructor ({ backendUrl, channel, platform, domain = location.host, cmpModel = 'tcfv2' }) {
    this.#backendUrl = backendUrl
    this.#channel = channel
    this.#platform = platform
    this.#domain = domain
    this.#cmpModel = cmpModel
  }

  /**
   * @param {SaveOpts} opts
   */
  saveTCString = async ({ uuid, tcString, consentDegree, nrvString, addtlConsent }) => {
    if (!this.#backendUrl) {
      log('backendUrl not defined, will not update backend')
      return
    }

    const tryToSend = async (data) => {
      try {
        log('trying to send update to backend. payload:', data)
        await request({
          method: 'POST',
          url: this.#backendUrl,
          headers: {
            'Content-Type': 'application/json'
          },
          data: JSON.stringify(data)
        })
        log('update sent to backend successfully')
        return true
      } catch (e) {
        log('send to backend failed with:', e.message)
        log('send to backend rescheduled 5s')
      }
    }
    /**
     * @type {PersistPayload}
     */
    const value = {
      addtlConsent,
      consent: consentDegree,
      domain: this.#domain,
      euconsent: tcString,
      nrvString,
      platform: this.#platform,
      uuid,
      version: 0,
      cmp: this.#cmpModel
    }
    if (this.#channel) {
      value.channel = this.#channel
    }

    await repeatUntil(() => tryToSend(value), 10000)
  }
}

/**
 * @typedef {Object} SaveOpts
 * @property {string} uuid
 * @property {string} tcString
 * @property {'yes'|'no'|'partial'} consentDegree
 * @property {string} addtlConsent
 * @property {string} nrvString
 */

/**
 * @typedef {Object} PersistOpts
 * @property {string|undefined} backendUrl
 * @property {string|undefined} channel
 * @property {string|undefined} platform
 * @property {string} [domain]
 * @property {CmpModel} cmpModel
 */

/**
 * @typedef {Object} PersistPayload
 * @property {string} addtlConsent
 * @property {'yes'|'no'|'partial'} consent
 * @property {string} domain
 * @property {string} euconsent
 * @property {string} nrvString
 * @property {string|undefined} platform
 * @property {string} uuid
 * @property {number} version
 * @property {string} [channel]
 * @property {CmpModel} cmp
 */

/**
 * @typedef {'tcfv2'|'lgpd'|undefined} CmpModel
 */
